import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { getNAICSCodeLabelGroups, type NaicsCodeId } from "@inrev/common";
import { useNaicsCodeGroupMap } from "@portal/api/naics-code";
import { cn } from "@portal/components/lib/utils";
import { Icon, Text } from "@portal/components/primitives";

type NAICSCodeListProps = {
	naicsCodes: NaicsCodeId[];
	emptyMessage?: string;
	className?: string;
	hideExpand?: boolean;
	showRatio?: boolean;
	showCount?: boolean;
	panelClassName?: string;
};

export const NAICSCodesList = ({
	naicsCodes,
	emptyMessage,
	className,
	hideExpand,
	showCount,
	showRatio,
	panelClassName,
}: NAICSCodeListProps) => {
	const groupMap = useNaicsCodeGroupMap();
	const naicsCodeLabelGroups = getNAICSCodeLabelGroups(new Set(naicsCodes), groupMap);

	if (!naicsCodeLabelGroups.length && emptyMessage === undefined) return <></>;
	return (
		<div
			className={cn(
				!!naicsCodes.length &&
					"flex flex-col w-fit min-w-[500px] max-w-[500px] divide-y divide-bc-default border border-bc-default bg-surface-light",
				className,
			)}
		>
			{!naicsCodes.length && emptyMessage !== undefined && (
				<Text type="body" color="tertiary">
					{emptyMessage}
				</Text>
			)}
			{naicsCodes.length > 0 &&
				naicsCodeLabelGroups.map((group, index) => (
					<Disclosure key={index} as="div">
						<div className="w-full">
							<DisclosureButton className="w-full flex items-center space-x-[10px] px-[15px] py-3 outline-none">
								{!hideExpand && (
									<Icon
										type="chevron-right"
										size="mini"
										color="tertiary"
										className="ui-open:rotate-90 ui-open:transform"
									/>
								)}
								<Text type="body" color="primary" className="flex-1 text-left">
									{group.label}
								</Text>
								<Text type="body" color="secondary">
									{showCount || showRatio ? `${group.subCodesLabels.length}` : ""}
									{showRatio ? `/ ${group.groupSubCodeCount}` : ""}
								</Text>
							</DisclosureButton>
							{!hideExpand && (
								<DisclosurePanel className="ml-[30px] px-[10px] py-[5px] mb-1 bg-surface-light rounded-sm">
									<div className={cn("flex flex-col space-y-2 [&>*]:shrink-0", panelClassName)}>
										{group.subCodesLabels.map((subCodeLabel, index) => (
											<Text
												key={index}
												type="body"
												color="tertiary"
												className="block w-full text-left [text-box-trim:unset]"
											>
												{subCodeLabel}
											</Text>
										))}
									</div>
								</DisclosurePanel>
							)}
						</div>
					</Disclosure>
				))}
		</div>
	);
};
